import { useLocation } from "react-router-dom";
import { useEffect } from "react";

/**
 * Soft-404 page.
 *
 * Lovable static hosting cannot return an HTTP 404 status for unknown
 * SPA routes — every request returns HTTP 200 with index.html. This
 * component delivers the strongest in-DOM signal we can to JS-capable
 * crawlers (Googlebot, Bingbot, GPTBot rendered mode, Perplexity):
 *
 *   1. Set `<html data-route-status="404">` so the external Valentine
 *      Digital SEO loader can detect 404 routes and skip injecting
 *      canonical / OG / Twitter / JSON-LD tags.
 *   2. Immediately strip any conflicting SEO already in the head
 *      (canonical, og:*, twitter:*, JSON-LD).
 *   3. Inject noindex tags marked with data-not-found-seo="true" so
 *      cleanup on unmount is exact.
 *   4. Run a scoped MutationObserver only while mounted so any late
 *      re-injection by the external loader is removed. The observer
 *      disconnects on unmount; we never try to restore deleted tags.
 *
 * Non-JS crawlers (LinkedIn, Slack, Facebook scraper) will still see
 * the raw index.html head. That is an accepted limitation documented
 * in `.lovable/plan.md`.
 */
const CONFLICT_SELECTORS = [
  'link[rel="canonical"]',
  'meta[property^="og:"]',
  'meta[name^="og:"]',
  'meta[name^="twitter:"]',
  'script[type="application/ld+json"]',
];
const CONFLICT_SELECTOR = CONFLICT_SELECTORS.join(", ");

function stripConflictingSeo() {
  document.head.querySelectorAll(CONFLICT_SELECTOR).forEach((node) => node.remove());
}

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
    );

    const previousTitle = document.title;
    document.documentElement.dataset.routeStatus = "404";

    stripConflictingSeo();

    document.title = "404 — Page Not Found | Thumb Premier Painting";

    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, follow");
    robots.setAttribute("data-not-found-seo", "true");
    document.head.appendChild(robots);

    const googlebot = document.createElement("meta");
    googlebot.setAttribute("name", "googlebot");
    googlebot.setAttribute("content", "noindex, follow");
    googlebot.setAttribute("data-not-found-seo", "true");
    document.head.appendChild(googlebot);

    // Scoped observer: while mounted, remove any newly-added conflicting
    // tag (likely re-injected by the external SEO loader). Does not
    // attempt to restore anything on its own.
    const observer = new MutationObserver((mutations) => {
      if (document.documentElement.dataset.routeStatus !== "404") return;
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(CONFLICT_SELECTOR)) node.remove();
        }
      }
    });
    observer.observe(document.head, { childList: true });

    return () => {
      observer.disconnect();
      document.head
        .querySelectorAll('[data-not-found-seo="true"]')
        .forEach((n) => n.remove());
      delete document.documentElement.dataset.routeStatus;
      document.title = previousTitle;
      // Signal valid-route renderers (and the ExternalSeoRefresher) to
      // rebuild normal SEO on the next page.
      window.dispatchEvent(new CustomEvent("route-status-change", { detail: { status: "ok" } }));
    };
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold">404</h1>
        <p className="mb-4 text-xl text-muted-foreground">Oops! Page not found</p>
        <a href="/" className="text-primary underline hover:text-primary/90">
          Return to Home
        </a>
      </div>
    </div>
  );
};

export default NotFound;
